import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './NameEditComponent.module.scss';
import cx from 'classnames';
import Icon from '../Icon/Icon';
import { MAX_NAME_LENGTH } from '../../../common/constants/common';
import { useHistory } from 'react-router-dom';
import { ENTER_KEY_CODE, ESCAPE_KEY_CODE } from '../../constants/common';
import Tooltip from '../Tooltip/Tooltip';

// variant: primary | secondary
const NameEditComponent = ({
  name,
  handleSaveName,
  size = 'large',
  isEditIcon,
  className,
  disableEditing,
  variant = 'primary',
  changeTextEdit = () => { },
}) => {
  const [edit, setEdit] = useState(false);
  const [fieldSize, setFieldSize] = useState(0);
  const [textHover, setTextHover] = useState(false);

  const history = useHistory();

  const inputRef = useRef(null);
  const textElement = useRef(null);

  const [allowHover, setAllowHover] = useState(false);

  const handleSave = useCallback(async () => {
    if (edit && inputRef.current) {
      await handleSaveName(inputRef.current.value);
      setEdit(false);
    }
  }, [handleSaveName, inputRef.current, edit]);

  const handleNameChange = e => {
    const value = e.target.value.substring(0, MAX_NAME_LENGTH);
    setFieldSize(value.length);
  };

  const startEditing = () => {
    setEdit(true);
    setTimeout(() => {
      inputRef.current.focus();
    }, 100)
  }

  const handleKeyDown = event => {
    if (event.keyCode === ENTER_KEY_CODE) {
      handleSave();
    }
    if (event.keyCode === ESCAPE_KEY_CODE) {
      setEdit(false);
    }
  };

  useEffect(() => {
    setFieldSize(name?.length);
    changeTextEdit(edit);
  }, [edit]);

  useEffect(() => {
    if (edit) {
      window.addEventListener('keydown', handleKeyDown);
    } else {
      window.removeEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [edit]);

  useEffect(() => {
    if (!disableEditing) {
      let unblock = edit
        ? history.block(({ pathname }) => {
            // If remaining a video is in progress, block navigation until new name is saved
            handleSave().then(() => {
              unblock();
              history.push(pathname);
            });
          })
        : history.block(() => {
            return true;
          });
    }
    if (!edit && textElement.current) {
      setAllowHover(isTextTruncated(textElement.current));
    }
  }, [edit]);

  function isTextTruncated(el) {
    const range = document.createRange();
    range.selectNodeContents(el);

    const containerRect = el.getBoundingClientRect();
    const textRects = range.getClientRects();

    for (const rect of textRects) {
      if (
        rect.right > containerRect.right ||
        rect.bottom > containerRect.bottom
      ) {
        return true;
      }
    }


    return false;
  }


  return <>
    <input
      ref={inputRef}
      className={cx(styles.editableName, styles[size],
        {
          [styles.visible]: edit
        }
      )}
      type="text"
      defaultValue={name}
      onChange={handleNameChange}
      size={fieldSize}
      maxLength={MAX_NAME_LENGTH}
      onBlur={handleSave}
    />
    <div className={cx(styles.nameContainer,
      {
        [styles.visible]: !edit
      },
      className)}
      onClick={!disableEditing && !isEditIcon && startEditing}
      onMouseOver={() => allowHover && setTextHover(true)}
      onMouseLeave={() => allowHover && setTextHover(false)}
    >
      <p ref={textElement} className={cx(styles.name, styles[size], styles[variant])}>{name}</p>
      {isEditIcon && !disableEditing && <Icon className={styles.pencilIcon} src="pencil" onClick={!disableEditing && startEditing} />}
      {!edit && allowHover && <Tooltip text={name} isHover={textHover} classNameTooltip={styles.tooltip} />}
    </div>
  </>
};

export default NameEditComponent;
